import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { distinctUntilChanged, map, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap, throttleTime } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators, } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent, PreviewMode, UtilityService, } from 'src/app/@shared';
import { EventService, Offer, OfferDomain, OfferService, OfferTag, Event, OfferDetailService, } from '../..';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { OfferPromo, PromoDomain, OfferPromoService } from '../..';
import { Status } from '../../models/offer-promo-status';
import * as dayjs from 'dayjs';
import { AuthorizeControlService, CommentsComponent } from 'pr1-ui-components';
import { IPrintPage } from '../../models/print-page.model';
import { WorkflowStage } from '../../models/workflow-stage.model';
import { ValidatorsService } from 'src/app/@shared/services/validators.service';
import { CustomFieldDefinition, CustomFieldType } from '../../models/custom-field.model';
import { CustomFieldService } from '../../services/custom-field.service';
import {
  EditUndoController,
  detailOnlyEditUndoPathFilter,
} from '../../services/edit-undo.controller';
import { isPropertyModifiedInList, getPreviousStepOldValue as lookupPreviousStepOldValue } from '../../utilities/previous-step-changes.util';

const log = new Logger('PromoDetailsComponent');

@Component({
  selector: 'app-promo-details',
  templateUrl: './promo-details.component.html',
  styleUrls: ['./promo-details.component.scss'],
})
export class PromoDetailsComponent<
  T extends Offer,
  TOfferDomain extends OfferDomain<Offer>,
  TOfferPromo extends OfferPromo,
  TEvent extends Event,
  TPromoDomain extends PromoDomain<OfferPromo>
> implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
  offer$: Observable<PromoDomain<OfferPromo>> = of(
    {} as PromoDomain<OfferPromo>
  );
  customFields$: Observable<CustomFieldDefinition[]> = of([]);
  customFieldValues: any = {};
  assetsforBadge: any;
  assetGroups = [];
  noneValue = null;

  tagCtrl = new FormControl<string>('', [
    Validators.minLength(1),
    Validators.maxLength(50),
  ]);
  offerTagIds: string[] = [];
  updatedOfferTagIds: string[] = [];
  formGroup = new FormGroup({
    // ParentOfferId: new FormControl<string | null>(''),
    Detail: new FormGroup({
      CustomFields: new FormGroup({}),
      ClientKey: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(200),
      ]),
      Id: new FormControl<string>(
        { value: '', disabled: true },
        { nonNullable: true }
      ),
      StartDate: new FormControl<Date | string | null>(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      EndDate: new FormControl<Date | string | null>(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      Rank: new FormControl<number>(1, [
        Validators.min(1),
        Validators.max(9999999),
      ]),
      Name: new FormControl<string>('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(100),
      ]),
      Headline: new FormControl<string>(''),
      BodyCopy: new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(100),
      ]),
      Disclaimer: new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(200),
      ]),
      ClientComment: new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(200),
      ]),
      TargetURL: new FormControl<string>('', [
        Validators.minLength(1),
        Validators.maxLength(5000),
      ]),
      Position: new FormControl<number | null>(null),
      PrintPageId: new FormControl<string | null>(null),
      AlternateText: new FormControl<string>(''),
      AdditionalText: this.fb.array([]),
      BadgeAssetId: new FormControl<string | null>(null),
      Status: new FormControl<string>('PENDING'),
      OfferVariantName: new FormControl<string | null>(null),
      ParentOfferGroupId: new FormControl<string | null>(null),
      OfferGroupAssociatedRank: new FormControl<number | null>(null),
    }),
  }, { updateOn: 'blur' });
  offerId: string = '0';
  eventId!: string;
  PreviewMode = PreviewMode;
  maxDate: Date = new Date(9999, 0, 1);
  private promoResponse: any;
  offerpromosList: TPromoDomain[] = [];
  versionsCount: number = 0;
  readOnlyMode: any = null;
  routeQueryParams: any = {};
  isCommentDialogOpened: boolean = false;
  printPagesList: IPrintPage[] = [];
  workflowStages: WorkflowStage[] = [];
  subscriptions = new Subscription();
  hasSaved = false;
  private exitSaveResult$ = new Subject<boolean>();
  private savingForExit = false;
  dateFieldsInCustomFields: string[] = [];
  selectFieldsInCustomFields: string[] = [];
  modifiedPropertyNames: string[] = [];
  modifiedPropertyOldValues: Record<string, string> = {};
  showChangesFromPreviousStep = false;

  private undoEnabled = false;
  private readonly editUndo: EditUndoController;
  private pendingCustomFieldsInit = false;
  private pendingUiInit = false;
  private destroy$ = new Subject<void>();

  get canUndo(): boolean {
    return this.undoEnabled && this.editUndo.hasUndoActions && this.canEdit();
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private offerService: OfferService<T, TOfferDomain>,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private matSnackBar: MatSnackBar,
    public utilityService: UtilityService,
    private eventService: EventService<TEvent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private authorizationService: AuthorizeControlService,
    private cdRef: ChangeDetectorRef,
    private offerDetailServ: OfferDetailService,
    private validatorsService: ValidatorsService,
    private customFieldService: CustomFieldService
  ) {
    this.editUndo = new EditUndoController({
      getSnapshot: () => this.buildUndoSnapshot(),
      applyChange: (path, value) => this.applyUndoChange(path, value),
      setCanUndo: (canUndo) => this.offerpromoService.setOfferEditCanUndo(canUndo),
      canUndo: () => this.canUndo,
      isPathTracked: detailOnlyEditUndoPathFilter,
    });
  }

  ngOnInit(): void {
    log.debug('init');
    this.offerpromoService.setOfferEditCanUndo(false);
    this.offerpromoService.commentDialogBoxStatus$.subscribe((status: boolean) => { this.isCommentDialogOpened = status });
    this.offerpromoService.eventId = this.route.snapshot.params['eventId'];
    this.loadCustomFields();

    this.subscriptions.add(
      this.offerpromoService.offerEditUndoEnabled$.subscribe((enabled) => {
        this.undoEnabled = !!enabled;
        this.editUndo.setEnabled(this.undoEnabled);
      })
    );
    this.subscriptions.add(
      this.offerpromoService.offerEditUndoRequest$.subscribe(() => {
        this.undoLastAction();
      })
    );
    // get the offer id from the parent route
    this.route.params.subscribe((params) => {
      this.offerId = params['offerId'];
      this.eventId = params['eventId'];
      this.getData();
      this.initLoad();
      this.getPrintPageDetailsByEvent();
    });

    this.offerService.variantsCount$.subscribe((res) => {
      this.versionsCount = res;
    });

    this.readOnlyMode = this.offerpromoService.getOfferDetailsReadOnlyValue();
    this.offerpromoService.offerDetailsReadOnlyMode$.subscribe((res) => {
      this.readOnlyMode = res;
    });
    this.offerpromoService.workflowStages$.subscribe(stages => {
      this.workflowStages = stages
    });
    this.formGroup.valueChanges.subscribe(() => {
      if (this.formGroup.valid) {
        this.offerpromoService.setOfferDetailsValidState = true;
      } else {
        this.offerpromoService.setOfferDetailsValidState = false;
      }
    });

    this.subscriptions.add(
      this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (!this.undoEnabled) return;
        if (this.editUndo.isApplyingUndo) return;
        if (this.editUndo.suppressCapture) return;
        if (this.pendingUiInit) return;
        if (this.pendingCustomFieldsInit) return;
        this.editUndo.captureIfNeeded();
      })
    );

    this.subscriptions.add(this.offerpromoService.deleteRecord$.subscribe((versionName) => {
      if (versionName) {
        this.deleteRecord(versionName);
      }
    }));

    this.subscriptions.add(
      this.offerService.modifiedPropertyNames$.subscribe((names) => {
        this.modifiedPropertyNames = names ?? [];
      })
    );
    this.subscriptions.add(
      this.offerService.modifiedPropertyOldValues$.subscribe((oldValues) => {
        this.modifiedPropertyOldValues = oldValues ?? {};
      })
    );
    this.subscriptions.add(
      this.offerService.showChangesFromPreviousStep$.subscribe((show) => {
        this.showChangesFromPreviousStep = show;
      })
    );
  }

  isPropertyModified(...propertyKeys: string[]): boolean {
    return isPropertyModifiedInList(
      this.modifiedPropertyNames,
      this.showChangesFromPreviousStep,
      ...propertyKeys
    );
  }

  getPreviousStepOldValue(...propertyKeys: string[]): string | null {
    return lookupPreviousStepOldValue(
      this.modifiedPropertyOldValues,
      this.showChangesFromPreviousStep,
      ...propertyKeys
    );
  }

  ngAfterViewInit() {
    const eventId = this.route.snapshot.params['eventId'];
  }

  ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.formGroup.dirty && !this.hasSaved && this.canEdit()) {
      $event.preventDefault();
      $event.returnValue = true;
    }
  }

  canExit(
    route: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (this.formGroup.dirty && !this.hasSaved && !this.canEdit()) {
      this.formGroup.markAsPristine();
      this.formGroup.markAsUntouched();
      return true;
    }

    if (this.formGroup.dirty && !this.hasSaved) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Close without saving?',
          message: 'You are attempting to navigate out of the Promo details tab with unsaved changes.\nHow would you like to proceed?',
          saveText: 'Save and Leave',
          confirmText: 'Leave Without Saving',
          cancelText: 'Stay',
        },
        disableClose: true,
      });

      return dialogRef.afterClosed().pipe(
        switchMap((result) => {
          if (result === true) {
            this.formGroup.markAsPristine();
            this.formGroup.markAsUntouched();
            this.hasSaved = false;
            return of(true);
          }
          if (result === 'save') {
            this.savingForExit = true;
            this.save();
            return this.exitSaveResult$.asObservable().pipe(take(1));
          }

          return of(false);
        })
      );
    }

    return true;
  }

  private resolveExitSave(success: boolean): boolean {
    if (!this.savingForExit) return false;
    this.savingForExit = false;
    this.exitSaveResult$.next(success);
    return true;
  }

  initLoad() {
    // this.getData();
    this.offerService.getAssets().subscribe((res: any) => {
      this.assetsforBadge = res.value;
    });

    this.subscriptions.add(this.offerService.saveorCancel$.pipe(throttleTime(100)).subscribe((res) => {
      if (res === '1') {
        this.save();
      } else if (res === '0') {
        this.cancel();
      }
    }));
  }

  getPrintPageDetailsByEvent() {
    this.offerpromoService.GetAllPrintPagesByEventforOffers(this.eventId).subscribe((res) => {
      this.printPagesList = res;
    })
  }

  navigateChildOffers() {
    this.router.navigate([`../../`, `promo`, `childversions`], {
      relativeTo: this.route, queryParamsHandling: 'preserve'
    });
  }

  getData() {
    if (this.offerId && this.offerId !== '0') {
      this.offer$ = this.offerDetailServ.offer$.pipe(distinctUntilChanged(),
        tap((offer: any) => {
          this.offerTagIds = [];
          this.updatedOfferTagIds = [];
          if (offer && this.offerId === offer?.Detail?.Id) {
            this.formGroup.controls['Detail'].controls['AdditionalText'] =
              this.fb.array([]);
            offer.EventOfferType = 'PROMO';
            this.offerService.currentRecord = JSON.parse(JSON.stringify(offer));
            if (offer && offer.Detail && offer.Detail.StartDate) {
              offer.Detail.StartDate = new Date(offer.Detail.StartDate);
            }
            if (offer && offer.Detail && offer.Detail.EndDate) {
              offer.Detail.EndDate = new Date(offer.Detail.EndDate);
            }
            const newVersionName = this.offerService.getNewVersion;
            if (newVersionName) {
              offer.Detail.ClientKey = '';
              offer.Detail.OfferVariantName = newVersionName;
            }
            this.customFieldValues = offer.Detail?.CustomFields || {};
            this.initUndoBaselineFromCurrentState();
            this.loadCustomFields();

            const additionalTexts = offer.Detail?.AdditionalText;
            delete (offer.Detail as any).AdditionalText;
            this.formGroup.patchValue(offer, { emitEvent: false });

            if (offer.ParentOfferGroupId == undefined) {
              offer.ParentOfferGroupId = null;
            }

            if (offer.OfferGroupAssociatedRank == undefined) {
              offer.OfferGroupAssociatedRank = -1;
            }
            this.formGroup.controls.Detail.controls.ParentOfferGroupId.patchValue(offer?.ParentOfferGroupId, { emitEvent: false });
            this.formGroup.controls.Detail.controls.OfferGroupAssociatedRank.patchValue(offer?.OfferGroupAssociatedRank, { emitEvent: false });
            this.promoResponse = offer;
            this.formGroup.controls.Detail.controls.PrintPageId.patchValue(offer?.PrintPageId || '', { emitEvent: false });
            this.formGroup.controls.Detail.controls.Position.patchValue(offer?.Position || 0, { emitEvent: false });
            if (offer.Position == -1) {
              this.formGroup.controls.Detail.controls.Position.patchValue(null, { emitEvent: false });
            }

            if (additionalTexts && additionalTexts.length > 0) {
              this.rebuildAdditionalTextFormArray(additionalTexts);
            } else {
              this.rebuildAdditionalTextFormArray(['']);
            }

            this.formGroup.controls.Detail.controls.BadgeAssetId.patchValue(
              offer?.BadgeAssetId as string,
              { emitEvent: false }
            );
            if (offer.OfferTagIds && offer.OfferTagIds.length) {
              this.offerTagIds = offer.OfferTagIds.filter((tagId: string): tagId is string => tagId !== null);
              this.updatedOfferTagIds = offer.OfferTagIds.filter((tagId: string): tagId is string => tagId !== null);
            } else {
              this.offerTagIds = [];
              this.updatedOfferTagIds = [];
            }
            this.formGroup.markAllAsTouched();
          }
        })
      );
    } else {
      this.eventService.getEventDomain(this.eventId).subscribe((res) => {
        this.formGroup.controls.Detail.controls.StartDate.patchValue(
          new Date(res?.Detail.StartDate as any)
        );
        this.formGroup.controls.Detail.controls.EndDate.patchValue(
          new Date(res?.Detail.EndDate as any)
        );
      });
      this.addAdditionalText('');
    }
  }

  validateDate(): boolean {
    let startDate = this.formGroup.controls.Detail.controls.StartDate;
    let endDate = this.formGroup.controls.Detail.controls.EndDate;
    if (endDate && endDate.value && startDate && startDate.value) {
      if (endDate.value < startDate.value) {
        this.matSnackBar.open(
          'End Date must be equal to or greater than Start Date',
          'Error',
          { verticalPosition: 'top', panelClass: ['snackbar-error'] }
        );
        return false;
      }
    }
    return true;
  }

  save() {
    if (!this.canEdit()) {
      this.resolveExitSave(true);
      return;
    }
    this.clearUndoStack();
    this.saveOffer();
    this.offerService.setSaveorCancel('');
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    });
  }

  formatDate(date: Date | string | null) {
    if (date) {
      const newDate = dayjs(date).format('MM/DD/YYYY');
      return newDate;
    } else {
      return '';
    }
  }

  saveOffer() {
    this.trimControlValues(this.formGroup);
    this.formGroup.markAsDirty();
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid) {
      if (!this.validateDate()) {
        this.resolveExitSave(false);
        return;
      }
      let promo = this.formGroup.getRawValue() as unknown as PromoDomain<TOfferPromo>;
      const startDate = this.formatDate(
        this.formGroup.controls.Detail.controls.StartDate.value
      );
      const endDate = this.formatDate(
        this.formGroup.controls.Detail.controls.EndDate.value
      );
      promo.Detail.StartDate = startDate;
      promo.Detail.EndDate = endDate;
      promo.Detail.Rank = promo.Detail.Rank || 1;
      promo.OfferTagIds = this.updatedOfferTagIds;
      promo.VersionIds = this.offerService.selectedVersionsIds;
      promo.BadgeAssetId = this.formGroup.controls.Detail.controls.BadgeAssetId
        .value as string;
      promo.EventId = this.eventId;
      delete promo.Detail.BadgeAssetId;

      const customFields = Object.keys(this.formGroup.controls.Detail.controls.CustomFields.controls);
      if (customFields && customFields.length > 0) {
        customFields.forEach((fieldName) => {
          if(this.dateFieldsInCustomFields.includes(fieldName) && this.formGroup.controls.Detail.controls.CustomFields.get(fieldName)?.value) {
            promo.Detail.CustomFields[fieldName] = this.formatDate(this.formGroup.controls.Detail.controls.CustomFields.get(fieldName)?.value);
          }
        });
      }

      promo.ParentOfferGroupId = this.formGroup.controls.Detail.controls.ParentOfferGroupId.value as string;
      promo.OfferGroupAssociatedRank = this.formGroup.controls.Detail.controls.OfferGroupAssociatedRank.value as number;
      const additionaltexts =
        promo.Detail.AdditionalText && promo.Detail.AdditionalText.length > 0
          ? promo.Detail.AdditionalText.filter(
            (i: any) => i.addtionalTextDetail
          ).map((x: any) => x.addtionalTextDetail)
          : [];
      promo.Detail.AdditionalText = additionaltexts;
      promo.Position = this.formGroup.controls.Detail.controls.Position.value;
      promo.PrintPageId = this.formGroup.controls.Detail.controls.PrintPageId.value;
      // const apiToCall = this.offerService.getNewVersion ? this.offerpromoService.clonePromo(promo.Detail.Id, promo.Detail) : this.offerpromoService.savePromo(promo);

      const objecttoPass =
        this.offerId !== '0' ? this.getUpdatedRecordInfo(promo) : promo;
      const offers =
        this.offerService.selection.selected &&
          this.offerService.selection.selected.length > 0
          ? this.offerService.selection.selected.map((x) => x.Id)
          : [this.offerId];

      let apiToCall;
      if (
        objecttoPass.Detail &&
        (!objecttoPass.Detail.Id || objecttoPass.Detail.Id === '0')
      ) {
        apiToCall = this.offerpromoService.savePromo(objecttoPass);
      } else {
        apiToCall = this.offerService.getNewVersion
          ? this.offerpromoService.clonePromo(promo.Detail.Id, promo)
          : this.offerpromoService.savePromoProperties(offers, objecttoPass);
      }
      apiToCall.subscribe({
        next: (response: any) => {
          this.hasSaved = true;
          this.formGroup.markAsPristine();
          if (this.savingForExit) {
            if (response?.Id) {
              this.offerId = response.Id;
            }
            this.offerService.reload();
            this.offerDetailServ.reload();
            this.offerpromoService.reload();
            this.resolveExitSave(true);
            return;
          }
          if (response) {
            const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
              data: {
                title: 'Saved Successfully',
                //message: 'Saved Successfully',
                confirmText: 'Continue Editing',
                cancelText: 'Back to Offers',
              },
              disableClose: true,
            });
            confirmDialog.afterClosed().subscribe((confirmResult) => {
              if (confirmResult) {
                this.routeQueryParams = this.route.snapshot.queryParams;
                this.offerId = response.Id;
                this.offerService.reload();
                this.offerDetailServ.reload();

                const currentUrlTree = this.router.createUrlTree(
                  [`../../../${response.Id}/promos/promodetails`],
                  {
                    relativeTo: this.route,
                    queryParams: this.routeQueryParams,
                    queryParamsHandling: 'merge',
                  }
                );

                this.router.navigateByUrl(currentUrlTree, { skipLocationChange: false }).then(() => {
                  this.formGroup.markAsPristine();
                  this.formGroup.markAsUntouched();
                  this.offerService.reload();
                  this.initLoad();
                  this.getPrintPageDetailsByEvent();
                });
              } else {
                this.offerpromoService.reload();
                this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
              }
            });
          }
        },
        error: (error) => {
          this.resolveExitSave(false);
          if (error.status === 500) {
            log.error('500 Error saving promo', error);
            this.matSnackBar.open('500 Error saving promo', 'Error', {
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;
            let messages: string[] = [];

            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                let propertyPath = validation.PropertyName || '';

                propertyPath = propertyPath.replace('PromoDomain.Detail', '').replace('Detail.', '');
                const message = validation.ErrorMessage || 'Validation error occurred';
                messages.push(message);

                const control = this.formGroup?.get(`Detail.${propertyPath}`) ||
                  this.formGroup?.get(propertyPath);

                if (control) {
                  control.markAsTouched();
                  control.setErrors({ invalid: message });
                }
              });
            }
            else if (apiValidations?.errors) {
              Object.keys(apiValidations.errors).forEach((key) => {
                const fieldErrors = apiValidations.errors[key];

                fieldErrors.forEach((msg: string) => messages.push(msg));

                const control = this.formGroup?.get(`Detail.${key}`);
                if (control) {
                  control.setErrors({ invalid: fieldErrors[0] });
                }
              });
            }

            else if (Array.isArray(apiValidations) && typeof apiValidations[0] === 'string') {
              messages = apiValidations;
            }

            else if (apiValidations?.Message) {
              messages.push(apiValidations.Message);
            }
            else {
              messages.push('Something went wrong. Please try again.');
            }

            this.matSnackBar.open(messages.join('\n'), 'OK', {
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
        },
      });
    } else if (this.savingForExit) {
      this.matSnackBar.open('Please fix validation errors before saving.', 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
      this.resolveExitSave(false);
    }
  }

  getUpdatedRecordInfo(record: any) {
    const objecttoPass: any = {
      Properties: {},
    };
    const currentPromoInfo = this.offerService.getCurrentRecord;
    if (currentPromoInfo) {
      const properties = Object.keys(record);
      for (let index = 0; index <= properties.length - 1; index++) {
        if (
          record[properties[index]] != null &&
          record[properties[index]] != undefined &&
          typeof record[properties[index]] === 'object' &&
          !Array.isArray(record[properties[index]])
        ) {
          const childProperties = Object.keys(record[properties[index]]);
          if (childProperties && childProperties.length > 0) {
            const currentRecord = currentPromoInfo[properties[index]];
            const updatedRecord = record[properties[index]];
            for (
              let childIndex = 0;
              childIndex <= childProperties.length - 1;
              childIndex++
            ) {
              if (
                (!Array.isArray(updatedRecord[childProperties[childIndex]]) &&
                  currentRecord[childProperties[childIndex]] !==
                  updatedRecord[childProperties[childIndex]]) ||
                (Array.isArray(updatedRecord[childProperties[childIndex]]) &&
                  !this.areArraysEqual(
                    currentRecord[childProperties[childIndex]],
                    updatedRecord[childProperties[childIndex]]
                  ))
              ) {
                objecttoPass['Properties'][childProperties[childIndex]] =
                  updatedRecord[childProperties[childIndex]];
              }
            }
          }
        } else if (
          (!Array.isArray(currentPromoInfo[properties[index]]) &&
            currentPromoInfo[properties[index]] !==
            record[properties[index]]) ||
          (Array.isArray(currentPromoInfo[properties[index]]) &&
            !this.areArraysEqual(
              currentPromoInfo[properties[index]],
              record[properties[index]]
            ))
        ) {
          objecttoPass[properties[index]] = record[properties[index]];
        }
      }
    }
    if ('Position' in objecttoPass) {
      objecttoPass.PrintPageId = record.PrintPageId;
    }
    return objecttoPass;
  }

  areArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();
    for (let i = 0; i < sortedArr1.length; i++) {
      if (sortedArr1[i] !== sortedArr2[i]) {
        return false;
      }
    }
    return true;
  }

  cancel(): void {
    this.offerService.setSaveorCancel('');
    if (this.formGroup.dirty && this.canEdit()) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Close without saving?',
          message: 'This promo has unsaved changes',
          confirmText: 'Continue Editing',
          cancelText: 'Close',
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe((confirmResult) => {
        if (!confirmResult) {
          this.clearUndoStack();
          this.formGroup.reset();
          this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        }
      });
    } else {
      this.clearUndoStack();
      this.formGroup.reset();
      this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    }
  }

  deleteRecord(versionName?: string) {
    if (versionName == 'Base' || this.canEdit() == false) return;
    const record = this.formGroup.getRawValue();
    const variantName = record.Detail?.OfferVariantName;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: variantName 
          ? `Are you sure you want to delete "${record.Detail.Name} (${variantName})"?`
          : `Are you sure you want to delete ${record.Detail.Name}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((confirmResult) => {
      if (confirmResult) {
        this.offerpromoService
          .deletePromo(record.Detail.Id, this.eventId)
          .subscribe({
            next: () => {
              this.matSnackBar.open(
                `${record.Detail.Name} deleted`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.offerpromoService.reload();
              this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
            },
            error: (error) => {
              log.error('Error deleting promo', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            },
          });
      }
    });
  }

  deleteBaseRecord() {
    if (this.canEdit() == false) {
      return;
    }
    const record = this.formGroup.getRawValue();
    const variantName = record.Detail?.OfferVariantName;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
         message: variantName 
          ? `Are you sure you want to delete "${record.Detail.Name} (${variantName})"?`
          : `Are you sure you want to delete ${record.Detail.Name}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((confirmResult) => {
      if (confirmResult) {
        this.offerpromoService
          .deleteBasePromo(record.Detail.Id, this.eventId)
          .subscribe({
            next: () => {
              this.matSnackBar.open(
                `${record.Detail.Name} deleted`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.offerpromoService.reload();
              this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
            },
            error: (error) => {
              log.error('Error deleting promo', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            },
          });
      }
    });
  }

  getUpdatedObject(selectedRecords: any) {
    let datatoReturn = [];
    if (selectedRecords && selectedRecords.length > 0) {
      for (let index = 0; index <= selectedRecords.length - 1; index++) {
        if (!selectedRecords[index].Detail) {
          datatoReturn.push({
            Detail: selectedRecords[index],
            EventOfferType: 'PROMO',
          });
        } else {
          datatoReturn.push(selectedRecords[index]);
        }
      }
    }
    return datatoReturn;
  }

  getCurrentValues(event: any, control: any) { }

  tagSelected(tagId: string): void {
    if (this.updatedOfferTagIds.includes(tagId)) {
      return;
    }
    this.applyUserOfferTagIds([...this.updatedOfferTagIds, tagId]);
  }

  addTag(offerTag: OfferTag): void {
    if (this.updatedOfferTagIds.includes(offerTag.Id)) {
      return;
    }
    this.applyUserOfferTagIds([...this.updatedOfferTagIds, offerTag.Id]);
    this.matSnackBar.open(`${offerTag.OfferTagName} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG);
  }

  removeTag(tagId: string): void {
    this.applyUserOfferTagIds(this.updatedOfferTagIds.filter(id => id !== tagId));
  }

  public get additionalTexts() {
    return this.formGroup.controls['Detail']?.controls[
      'AdditionalText'
    ] as FormArray;
  }

  addAdditionalText(datatoAdd: string, emitEvent = true) {
    const mutator = () => this.pushAdditionalTextRow(datatoAdd, emitEvent);
    if (emitEvent) {
      this.applyUserAdditionalTextMutation(mutator);
    } else {
      mutator();
    }
  }

  private pushAdditionalTextRow(datatoAdd: string, emitEvent: boolean): void {
    const formtoAdd = this.fb.group(
      {
        addtionalTextDetail: [
          datatoAdd,
          [Validators.minLength(1), Validators.maxLength(200)],
        ],
      },
      { updateOn: 'blur' }
    );
    this.additionalTexts.push(formtoAdd, { emitEvent });
  }

  private rebuildAdditionalTextFormArray(items: any[]): void {
    this.editUndo.runSuppressed(() => {
      while (this.additionalTexts.length > 0) {
        this.additionalTexts.removeAt(0, { emitEvent: false });
      }
      const texts =
        items && items.length > 0
          ? items.map((item) =>
              typeof item === 'string' ? item : item?.addtionalTextDetail ?? ''
            )
          : [''];
      texts.forEach((text) => this.addAdditionalText(text, false));
    });
  }

  deleteAddtionalText(index: number) {
    if (this.additionalTexts.controls.length > 1) {
      this.applyUserAdditionalTextMutation(() => {
        this.additionalTexts.removeAt(index);
      });
    }
  }


  canEdit(): boolean {
    const hasAccess =
      this.authorizationService.checkAccess('edit||offermang') &&
      this.readOnlyMode != true;
    if (hasAccess) {
      this.formGroup.disabled ? this.formGroup.enable() : null;
      this.tagCtrl.enable();
    } else {
      this.formGroup.enabled ? this.formGroup.disable() : null;
      this.tagCtrl.disable();
    }
    return hasAccess;
  }

  private loadCustomFields(): void {
    this.customFields$ = this.customFieldService.getCustomFieldDefinitionsByVertexType('PROMO').pipe(
      tap((fields: CustomFieldDefinition[]) => {
        const finalizeAfterCustomFieldsLoaded = this.pendingCustomFieldsInit;
        this.editUndo.runSuppressed(() => {
          const customFieldsGroup = this.formGroup.controls.Detail.controls.CustomFields as FormGroup;

          fields.forEach((field: CustomFieldDefinition) => {
            if (!customFieldsGroup.get(field.FieldName)) {
              const formControl = new FormControl<any>({ value: null, disabled: field.IsReadOnly });
              if (field.FieldType === CustomFieldType.Toggle || field.FieldType === CustomFieldType.Checkbox) {
                formControl.setValue(false, { emitEvent: false });
              }
              customFieldsGroup.addControl(field.FieldName, formControl);
              if (field.FieldType === CustomFieldType.Date) {
                this.dateFieldsInCustomFields.push(field.FieldName);
              }
              if (field.FieldType === CustomFieldType.MultiSelect) {
                this.selectFieldsInCustomFields.push(field.FieldName);
              }
            }
          });
          if (Object.keys(this.customFieldValues).length > 0) {
            Object.keys(this.customFieldValues).forEach(key => {
              if (customFieldsGroup.get(key)) {
                if (this.dateFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                  customFieldsGroup.get(key)?.setValue(new Date(this.customFieldValues[key]), {
                    emitEvent: false,
                  });
                } else if (this.selectFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                  customFieldsGroup.get(key)?.setValue(
                    typeof this.customFieldValues[key] === 'string'
                      ? [this.customFieldValues[key]]
                      : this.customFieldValues[key],
                    { emitEvent: false }
                  );
                } else {
                  customFieldsGroup.get(key)?.setValue(this.customFieldValues[key], { emitEvent: false });
                }
              }
            });
          }
        });

        if (finalizeAfterCustomFieldsLoaded) {
          this.pendingCustomFieldsInit = false;
          this.tryFinalizeUndoBaseline();
        }
      })
    );
  }

  getCustomFieldsGroup(): FormGroup {
    return this.formGroup.controls.Detail.controls.CustomFields as FormGroup;
  }
  
  openDialog($event: any, offer: any) {
    if (this.isCommentDialogOpened == false) {
      let commentElementStyle = document.getElementById(offer.DomainId) as HTMLElement;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.hasBackdrop = true;
      dialogConfig.disableClose = false;
      //dialogConfig.position = { left: $event.clientX + 'px', top: $event.clientY + 'px' };
      dialogConfig.data = {
        headline: offer.Detail.Headline,
        channel: offer.EventOfferType,
        parentId: offer.Detail.Id,
        clientKey: offer.Detail.ClientKey,
        bodyCopy: offer.Detail.BodyCopy,
        workflowStage: this.workflowStages.length ? this.workflowStages[0] : null,
        // position:{ left: $event.clientX + 'px', top: $event.clientY + 'px' }
      };
      let confirmDialog = this.dialog.open(CommentsComponent, dialogConfig);
      confirmDialog.afterOpened().subscribe(() => {
        if (commentElementStyle) {
          commentElementStyle.style.color = "blue";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(true);
        }
      });
      confirmDialog.afterClosed().subscribe((data) => {
        if (data > 0) {
          Object(offer).CommentsCount = data;
        }
        if (commentElementStyle) {
          commentElementStyle.style.color = "black";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(false);
        }
      });
    }
  }
  clearUndoStack() {
    this.editUndo.clearStack();
  }

  undoLastAction() {
    this.editUndo.undoLast();
  }

  onDateCommitted() {
    this.captureUndoOnFieldCommit();
  }

  onRankCommitted() {
    this.captureUndoOnFieldCommit();
  }

  onAdditionalTextCommitted(): void {
    this.captureUndoOnFieldCommit();
  }

  private captureUndoOnFieldCommit(): void {
    this.editUndo.captureOnFieldCommit(() => this.canCaptureUndoForUserActions());
  }

  private applyUndoChange(path: string, value: any) {
    if (path === 'updatedOfferTagIds') {
      this.applyOfferTagIds(Array.isArray(value) ? value : []);
      return;
    }

    if (path === 'Detail.AdditionalText') {
      this.rebuildAdditionalTextFormArray(Array.isArray(value) ? value : []);
      return;
    }

    const ctrl = this.formGroup.get(path);
    if (ctrl) {
      ctrl.setValue(value, { emitEvent: false });
      ctrl.updateValueAndValidity({ emitEvent: false });
      ctrl.markAsDirty();
      ctrl.markAsTouched();
    }
  }

  private buildUndoSnapshot(): any {
    return {
      Detail: this.formGroup.getRawValue()?.Detail ?? {},
      updatedOfferTagIds: [...(this.updatedOfferTagIds || [])],
    };
  }

  private initUndoBaselineFromCurrentState() {
    this.clearUndoStack();
    if (!this.undoEnabled) {
      this.pendingCustomFieldsInit = false;
      this.editUndo.suppressCapture = false;
      return;
    }
    this.pendingCustomFieldsInit = true;
    this.editUndo.suppressCapture = true;
    this.pendingUiInit = true;
    setTimeout(() => {
      this.pendingUiInit = false;
    });
  }

  private tryFinalizeUndoBaseline(): void {
    if (!this.undoEnabled) return;
    if (this.pendingCustomFieldsInit) return;

    this.editUndo.finalizeBaselineFromLoad();
  }

  private applyOfferTagIds(tagIds: string[]): void {
    const next = [...tagIds];
    this.updatedOfferTagIds = next;
    this.offerTagIds = [...next];
  }

  private canCaptureUndoForUserActions(): boolean {
    return (
      this.undoEnabled &&
      !this.editUndo.isApplyingUndo &&
      !this.editUndo.suppressCapture &&
      !this.pendingUiInit &&
      !this.pendingCustomFieldsInit
    );
  }

  private areTagIdListsEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, index) => id === b[index]);
  }

  private applyUserOfferTagIds(nextIds: string[]): void {
    const normalized = [...nextIds];
    if (this.areTagIdListsEqual(this.updatedOfferTagIds, normalized)) {
      return;
    }

    this.editUndo.applyUserMutation(
      () => this.applyOfferTagIds(normalized),
      () => this.canCaptureUndoForUserActions()
    );
  }

  private applyUserAdditionalTextMutation(mutator: () => void): void {
    this.editUndo.applyUserMutation(mutator, () => this.canCaptureUndoForUserActions());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearUndoStack();
    this.offerpromoService.deleteRecord("");
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  onRankInput(event: any) {
    const filtered = this.validatorsService.allowOnlyDigits(event);
    const numericValue = filtered ? Number(filtered) : null;
    this.formGroup.controls.Detail.controls['Rank']?.setValue(numericValue, { emitEvent: false });
  }
}
